import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import { Company, CompanyMetricsRecord } from 'src/models';
import { HEADSETS, HOTELS } from 'src/constants';
import { compareString, hybridCompare } from 'src/helper';
import { usePage, useLanguage, useContextData } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { clearCompanyMetricsState } from 'src/store/reducers/companyMetrics';
import { clearReportsHeadsetsState } from 'src/store/reducers/reportHeadsets';
import { useCompanyById } from 'src/services/company';
import {
  useCompanyMetrics,
  useCompanyMetricsState,
  fetchMetricsAction,
} from 'src/services/companyMetrics';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  useStickyCols,
} from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
  useDateRangePicker,
  Link,
} from 'src/components/general';
import styles from './hotelMetricsStyle.module.scss';

export const HotelMetrics = () => {
  const { hotel: companyId } = useParams() as { hotel: string };
  const {
    header: { width: headerWidth },
    navigator: { setBackUrl },
  } = useContextData();
  const records = useCompanyMetrics();
  const state = useCompanyMetricsState();
  const company = useCompanyById(companyId) as Company | null;
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const {
    access: { accessMap },
  } = usePage();

  const [isLoading, setIsLoading] = useState(state.isPending);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
    clearDRP,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    disabled: isLoading,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  const data: CompanyMetricsRecord[] = useMemo(
    () =>
      records.map((r) => ({
        ...r,
        takeToSawPackages: (r.uiFullAccess / r.sessions) * 100,
        sawPackagesToInterested: r.uiFullAccess
          ? (r.uiFullAccessSpecific / r.uiFullAccess) * 100
          : 0,
        interestedToPay: r.paidSessions
          ? (r.uiFullAccessSpecific / r.paidSessions) * 100
          : 0,
      })),
    [records],
  );

  const summaryRecord: CompanyMetricsRecord = useMemo(
    () =>
      data.reduce(
        (value, record) => {
          value.uiFullAccess += record.uiFullAccess;
          value.uiFullAccessSpecific += record.uiFullAccessSpecific;
          value.paidSessions += record.paidSessions;
          value.sessionsWithInvoice += record.sessionsWithInvoice;
          value.sessions += record.sessions;
          return value;
        },
        {
          id: '-1',
          deviceId: '-1',
          label: t('general.total'),
          roomLabel: '',
          sessionsWithInvoice: 0,
          paidSessions: 0,
          sessions: 0,
          uiFullAccess: 0,
          uiFullAccessSpecific: 0,
        },
      ),
    [data, t],
  );

  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    if (!company) {
      return;
    }

    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    setIsLoading(true);
    setIsFiltered(isRangeChanged);

    dispatch(
      fetchMetricsAction({
        companyId,
        params: {
          start,
          end,
        },
      }),
    );
  }, [dispatch, companyId, range, company, isRangeChanged]);

  useEffect(() => {
    setIsLoading(state.isPending);
  }, [state.isPending]);

  useEffect(
    () => () => {
      dispatch(clearCompanyMetricsState());
    },
    [dispatch],
  );

  const config = useMemo<StickyTableConfig<CompanyMetricsRecord>[]>(() => {
    return [
      {
        title: t('page.hotel.metrics.label'),
        key: 'label',
        field: 'label',
        sorter: (a, b, sortOrder) =>
          hybridCompare(a.label, b.label) * (sortOrder === 'descend' ? -1 : 1),
        render: (value, headset) => (
          <Link
            className={cl(
              styles.label,
              !accessMap[HEADSETS].length && styles.inactive,
            )}
            disabled={headset.id === '-1'}
            src={
              accessMap[HEADSETS].length
                ? `/${HOTELS}/${companyId}/${HEADSETS}/${headset.id}/${accessMap[HEADSETS][0]}`
                : null
            }
            onClick={() => {
              if (!accessMap[HEADSETS].length) {
                return;
              }

              dispatch(clearReportsHeadsetsState());
              setBackUrl(null);
            }}
          >
            {value || '-'}
          </Link>
        ),
      },
      {
        title: t('page.hotel.metrics.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        sorter: (a, b, sortOrder) =>
          compareString(a.roomLabel, b.roomLabel) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => value || '-',
      },
      {
        title: t('page.hotel.metrics.sessions'),
        key: 'sessions',
        field: 'sessions',
        sorter: (a, b, sortOrder) =>
          (a.sessions - b.sessions) * (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.hotel.metrics.sessionsWithInvoice'),
        key: 'sessionsWithInvoice',
        field: 'sessionsWithInvoice',
        sorter: (a, b, sortOrder) =>
          (a.sessionsWithInvoice - b.sessionsWithInvoice) *
          (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.hotel.metrics.paidSessions'),
        key: 'paidSessions',
        field: 'paidSessions',
        sorter: (a, b, sortOrder) =>
          (a.paidSessions - b.paidSessions) *
          (sortOrder === 'descend' ? -1 : 1),
      },
    ];
  }, [companyId, t, accessMap, dispatch, setBackUrl]);

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applyDRPChanges();
          applySCChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        {modalDateRangePicker}
        {modalStickyColsFilter}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HOTELS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(rangeIndicatorText)}
              disabled={isLoading}
            />
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              clear={clearDRP}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className={styles.filters}>
          {dateRangePicker}
          {stickyColsFilter}
        </div>
      </div>
      <StickyTable
        config={config}
        data={data}
        summaryRecord={summaryRecord}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
        isFiltered={isFiltered}
        resetFilter={resetDRPToInit}
        notFoundText={t('general.resetFilters')}
      />
    </div>
  );
};

export default HotelMetrics;
